import { produce, freeze } from 'immer';
import { storageRepository } from '@betgames/bg-tools';
import type {
    IStoreOptions,
    IValueOrUpdater,
    Updater,
    Subscriber,
    IPersistStore,
} from './interfaces';
import { storesRegistry } from './tools/storesRegistry';
import { shallowEqual } from './utils/shallowEqual';
import { getWhiteBlackListedStore } from './utils/getWhiteBlackListedStore';

const DEFAULT_VERSION = -1;

export default class Store<State> {
    private readonly state: { value: State };

    private readonly subscriptions: Set<Subscriber<State>> = new Set();

    private readonly persistKey: string;

    constructor(private readonly options: IStoreOptions<State>) {
        let persisted: State;

        if (options.persist) {
            this.persistKey = `store:${options.key}`;
            const cache = storageRepository.getItemParsed<IPersistStore<State>>(this.persistKey);
            const cachedStore = cache?.persistedStore || cache;
            const isValidCache = typeof cachedStore !== 'undefined' && cachedStore !== null;
            const cachedVersion = cache?.version !== undefined ? cache.version : DEFAULT_VERSION;
            const currentVersion = options.migration?.version || cachedVersion;

            if (isValidCache) {
                if (options.migration?.version > cachedVersion) {
                    try {
                        persisted = options.migration.callback(cachedStore as State);
                        storageRepository.setItemParsed(this.persistKey, {
                            version: options.migration.version,
                            persistedStore: getWhiteBlackListedStore<State>(
                                persisted,
                                options.whitelist,
                                options.blacklist,
                            ),
                        });
                    } catch (error) {
                        persisted = null;
                    }
                } else {
                    persisted = cachedStore as State;
                }
            }

            if (
                (options.blacklist || options.whitelist) &&
                typeof persisted === 'object' &&
                persisted !== null
            ) {
                persisted = {
                    ...options.default,
                    ...getWhiteBlackListedStore<State>(
                        persisted,
                        options.whitelist,
                        options.blacklist,
                    ),
                };
            }

            this.subscribe((newState) => {
                storageRepository.setItemParsed(this.persistKey, {
                    version: currentVersion,
                    persistedStore: getWhiteBlackListedStore<State>(
                        newState,
                        options.whitelist,
                        options.blacklist,
                    ),
                });
            });
        }

        this.state = {
            value: freeze(persisted || options.default),
        };

        storesRegistry.addStore(options.key, this);
    }

    public get value(): State {
        return this.state.value;
    }

    public update(valueOrUpdater: IValueOrUpdater<State>): void {
        const newState =
            typeof valueOrUpdater === 'function'
                ? produce(this.state.value, valueOrUpdater as Updater<unknown>)
                : freeze(valueOrUpdater);

        if (!shallowEqual(this.state.value, newState)) {
            this.state.value = newState;
            this.subscriptions.forEach((subscription) => {
                subscription(this.state.value);
            });
        }
    }

    public reset(): void {
        this.update(this.options.default);
    }

    public subscribe(subscriber: Subscriber<State>): () => void {
        this.subscriptions.add(subscriber);

        return () => {
            this.subscriptions.delete(subscriber);
        };
    }

    public unsubscribe(subscriber: Subscriber<State>): void {
        this.subscriptions.delete(subscriber);
    }
}
